import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { PrimaryButton, SecondaryButton, AgTableCard, Input } from "../../UI";
import { useUserContext } from "../../../contexts/UserContext";
import Loader from "../../UI/Loader/Loader";
import ErrorAlert from "../../UI/Alert/ErrorAlert";
import SuccessAlert from "../../UI/Alert/SuccessAlert";
import useComponentVisible from "../../UI/ComponentVisible";
import { permission } from "../../../config/permission";
import DeleteTealiumAudienceStream from "./DeleteTealiumAudienceStream";
import ConfirmDeleteTealiumAudienceStream from "./ConfirmDeleteTealiumAudienceStream";
import { getAllTealiumAudienceStreamData } from "../../../utils/tealiumAPIs";
import { getAllTealiumProduct, getTealiumAudienceStream } from "../../../utils/APIs";
import AdobeTargetAudienceConfigModel from "../../AdobeTargetAudienceConfigRule/ConfigRuleModel";
import { generateTeliumAudienceStreadName } from "../../../utils/helper";
import AddToAdobeTargetModel from "./AddToAdobeTargetModal";
import { formatDate } from "../../../utils/converter";
import UpdateAdobeTargetAudienceModal from "./UpdateAdobeTargetAudienceModal";
import { toast } from "react-toastify";
import { useContentfulContext } from "../../../contexts/ContentfulContext";

const TealiumAudienceStreamTable = () => {
  const navigate = useNavigate();
  const { userData, checkPermission } = useUserContext();
  const { enums, content } = useContentfulContext();

  const [deleteIntegration, setDeleteIntegration] = useState(false);
  const [tealiumAudienceStreamTableData, setTealiumAudienceStreamTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [handleId] = useState("");
  const [confirmModal, setConfirmModal] = useState({
    active: false,
    deactivate: false,
    delete: false,
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [menuPosition] = useState({ top: 0, left: 0 });
  const [gridApi, setGridApi] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);

  const { ref, isComponentVisible } = useComponentVisible(false);
  const defaultSelectedTealium = userData?.defaultTealiumKey;
  const [, setTealiumApiData] = useState([]);
  const [isATConfigRuleChange, setIsATConfigRuleChange] = useState(false);
  const [dataForAddToAdobeTargetModal, setDataForAddToAdobeTargetModal] = useState([]);
  const [isAddToAdobeTargetModalOpen, setIsAddToAdobeTargetModalOpen] = useState(false);
  const [showAdobeTargetAudience, setShowAdobeTargetAudience] = useState(false);
  const [audienceData, setAudienceData] = useState();

  const navigator = useNavigate();

  useEffect(() => {
    if (defaultSelectedTealium) getTealiumAudienceStreamData()
  }, [defaultSelectedTealium])

  const handleToggleConfigModel = () => {
    setIsATConfigRuleChange(!isATConfigRuleChange);
  }

  const getTealiumAudienceStreamData = async () => {
    try {
      setLoading(true);
      const res = await getAllTealiumAudienceStreamData(defaultSelectedTealium.tealium_account, defaultSelectedTealium.tealium_profile);
      const tealiumAudienceStream = await getTealiumAudienceStream(userData.token, userData.selectedOrganization.org_token, userData.defaultAdobeIO?._id)

      const map = new Map();
      tealiumAudienceStream.data.forEach((x) => {
        map.set(x.tealium_as_audience_id, x);
      })

      if (res?.audiences) {
        const update = res.audiences.map((x) => {
          const data = map.get(x.id);
          if (data)
            return {
              ...x,
              ...data
            }
          else return x
        })
        setTealiumAudienceStreamTableData(update)
      } else {
        setTealiumAudienceStreamTableData([])
      }
    } catch (error) {
      setError(error?.message || content?.COMMON_MESSAGE.something_wrong_msg)
    } finally {
      setLoading(false);
    }
  }

  const handleAddAdobeTarget = async () => {
    if (selectedRecords?.length == 0) {
      setError(content.TEALIUM_AUDIENCE_STREAM_CONTENT.error.FE135);
      return;
    }
    if(!userData.defaultAdobeIO){
      setError(content.TEALIUM_AUDIENCE_STREAM_CONTENT.error.FE136);
      return;
    }
    if(userData?.atAudienceConfigRule.length === 0){
      setError(content.TEALIUM_AUDIENCE_STREAM_CONTENT.error.FE137);
      return;
    }

    const dataForModalDisplay = selectedRecords.map((singleSelectedRecord) => ({
      as_audience_id: singleSelectedRecord.id,
      as_audience_name: singleSelectedRecord.name,
      at_audience_name: generateTeliumAudienceStreadName(userData?.atAudienceConfigRule?.[0]?.naming_convention, singleSelectedRecord.id, singleSelectedRecord.name),
      description: '',
      workspace: ''
    }));
    setDataForAddToAdobeTargetModal(dataForModalDisplay);
    setIsAddToAdobeTargetModalOpen(true);
  };

  const colDefs = [
    {
      headerName: "",
      field: "",
      checkboxSelection: ({data}) => !data?.at_audience_id,
      suppressMovable: true,
    },
    {
      headerName: content.TEALIUM_AUDIENCE_STREAM_CONTENT.integration_table_headers.id,
      field: "id",
      resizable: true,
      suppressMovable: true,
      cellRenderer: ({ data }) => {
        return <div className="relative inline-block text-left">
          { data.at_audience_id ? <button className='text-center block cursor-pointer text-primary' onClick={(e) => {
            setShowAdobeTargetAudience(true);
            setAudienceData(data);
          }}>{ data.id }</button> : data.id }
        </div>;
      }
    },
    {
      headerName: content.TEALIUM_AUDIENCE_STREAM_CONTENT.integration_table_headers.name,
      field: "name",
      resizable: true,
      suppressMovable: true,
      filter: true,
      searchable: true
    },
    {
      headerName: content.TEALIUM_AUDIENCE_STREAM_CONTENT.integration_table_headers.at_audience_id,
      field: "at_audience_id",
      resizable: true,
      suppressMovable: true,
      filter: true,
    },
    {
      headerName: content.TEALIUM_AUDIENCE_STREAM_CONTENT.integration_table_headers.at_audience_name,
      field: "at_audience_name",
      resizable: true,
      suppressMovable: true,
      filter: true,
    },
    {
      headerName: content.TEALIUM_AUDIENCE_STREAM_CONTENT.integration_table_headers.at_audience_workspace,
      field: "at_audience_workspace",
      resizable: true,
      suppressMovable: true,
    },
    {
      headerName: content.TEALIUM_AUDIENCE_STREAM_CONTENT.integration_table_headers.updated_at,
      field: "updatedAt",
      cellRenderer: ({ data }) => data.updatedAt ? formatDate(data.updatedAt) : "",
      comparator: (date1, date2) => new Date(date1) - new Date(date2),
      resizable: true,
      suppressMovable: true,
    },
    {
      headerName: content.TEALIUM_AUDIENCE_STREAM_CONTENT.integration_table_headers.last_edited_by,
      field: "last_edited_by",
      cellRenderer: ({ data }) => data?.last_edited_by?.name, 
      resizable: true,
      suppressMovable: true,
    },
  ];

  const onFilterTextBoxChanged = (event) => {
    if (!gridApi) return;
    const filterValue = event.target.value.toLowerCase();

    const filterModel = {
      name: { type: "contains", filter: filterValue },
    };
    
    gridApi.setFilterModel(filterModel);
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const handleRowSelected = (event) => {
    const selectedRows = event.api.getSelectedRows();
    setSelectedRecords(selectedRows);
  };

  const autoSizeStrategy = useMemo(() => {
    return {
      type: window.innerWidth < 1024 ? "fitCellContents" : "fitGridWidth",
      defaultMinWidth: 40,
      columnLimits: [
        { colId: "id", minWidth: 250 },
        { colId: "name", minWidth: 300 },
        { colId: "at_audience_id", minWidth: 150 },
        { colId: "at_audience_name", minWidth: 200 },
        { colId: "at_audience_workspace", minWidth: 200 },
        { colId: "updatedAt", minWidth: 200 },
        { colId: "last_edited_by", minWidth: 200 },
      ],
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setError('');
      setSuccess('');
    }, content.TEALIUM_AUDIENCE_STREAM_CONTENT.timeout);

    return () => clearTimeout(timer);
  }, [error, success]);

  const handleCloseAddToAdobeTargetModal = () => {
    setDataForAddToAdobeTargetModal([]);
    setIsAddToAdobeTargetModalOpen(false);
  }
  return (
    <>
      { showAdobeTargetAudience && (
        <UpdateAdobeTargetAudienceModal 
          setShowAdobeTargetAudience={setShowAdobeTargetAudience} 
          audienceData={audienceData}
          success={success}
          setSuccess={setSuccess}
          getTealiumAudienceStreamData={getTealiumAudienceStreamData}
        />)}
      {isAddToAdobeTargetModalOpen && (
        <AddToAdobeTargetModel
          showAddToAdobeTargetModel={isAddToAdobeTargetModalOpen}
          addToAdobeTargetModalData={dataForAddToAdobeTargetModal}
          handleCloseModal={handleCloseAddToAdobeTargetModal}
          getTealiumAudienceStreamData={getTealiumAudienceStreamData}
          atAudienceConfigRule={userData?.atAudienceConfigRule?.[0]}
          setSelectedRecords={setSelectedRecords}
        />)}
      {isATConfigRuleChange &&
        (<AdobeTargetAudienceConfigModel
          setSuccess={setSuccess}
          handleCloseModal={handleToggleConfigModel}
          showAdobeTargetAudienceConfigModel={isATConfigRuleChange} />)}
      {error && (
        <ErrorAlert timeout={content.TEALIUM_AUDIENCE_STREAM_CONTENT.timeout}>
          {error}
        </ErrorAlert>
      )}
      {success && (
        <SuccessAlert disappearTime={content.TEALIUM_AUDIENCE_STREAM_CONTENT.timeout}>
          {success}
        </SuccessAlert>
      )}

      {userData?.tealiumIntegrations !== 0 && (
        <div className="relative">
          <AgTableCard
            header={
              <div className="flex flex-col md:flex-row justify-between w-full items-start md:items-center gap-4">
                <div className="w-full md:w-80">
                  <Input
                    placeholder={content.TEALIUM_AUDIENCE_STREAM_CONTENT.search_btn}
                    onChange={onFilterTextBoxChanged}
                    prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                    clearable
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <SecondaryButton handleClick={handleToggleConfigModel}>{content.TEALIUM_AUDIENCE_STREAM_CONTENT.audience_config_btn}</SecondaryButton>
                  {checkPermission(permission.AUDIENCE_TEALIUM_AUDIENCE_STREAM_OP_ADD_TO_ADOBE_TARGET) && (
                    <PrimaryButton handleClick={handleAddAdobeTarget}>
                      {content.TEALIUM_AUDIENCE_STREAM_CONTENT.adobe_target_btn}
                    </PrimaryButton>
                  )}
                </div>
              </div>
            }
            tableId="tealium-audience-stream-table"
            loading={loading}
            emptyMessage={content.TEALIUM_AUDIENCE_STREAM_CONTENT.error.FE133}
            rowData={tealiumAudienceStreamTableData}
            columnDefs={colDefs}
            autoSizeStrategy={autoSizeStrategy}
            onGridReady={onGridReady}
            rowSelection="multiple"
            suppressRowClickSelection
            onRowSelected={handleRowSelected}
            pagination={true}
            paginationPageSize={content.TEALIUM_AUDIENCE_STREAM_CONTENT.default_page_size}
            paginationPageSizeSelector={enums?.PAGINATION_PAGE_SIZE_SELECTOR}
          />
          {isComponentVisible && (
            <div
              ref={ref}
              className="absolute z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
              style={{ top: menuPosition.top, left: menuPosition.left - 192 }}
            >
              <div className="py-1">
                {checkPermission(permission.EDIT_TEALIUM_AUDIENCE_STREAM) && (
                  <p
                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                    tabIndex="-1"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/setup/tealium/edit/${handleId}`);
                    }}
                  >
                    {content.TEALIUM_AUDIENCE_STREAM_CONTENT.more_info_options.edit}
                  </p>
                )}
                {checkPermission(permission.DELETE_TEALIUM_AUDIENCE_STREAM) && (
                  <p
                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                    tabIndex="-1"
                    onClick={() => {
                      setDeleteIntegration(true);
                    }}
                  >
                    {content.TEALIUM_AUDIENCE_STREAM_CONTENT.more_info_options.delete}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {actionLoading && <Loader />}
      {userData?.tealiumIntegrations == 0 && <div className='text-center flex flex-col items-center content-center !mt-20'>
        <p className="text-xl leading-tight tracking-tight text-black text-center my-4">
          {content.TEALIUM_AUDIENCE_STREAM_CONTENT.error.FE134}
        </p>
        <PrimaryButton handleClick={() => {
          navigator('/setup/tealium-api-key-integrations');
        }} cl>
          {content.TEALIUM_AUDIENCE_STREAM_CONTENT.create_new_integration_cta_text}
        </PrimaryButton>
      </div>}

      {deleteIntegration && (
        <DeleteTealiumAudienceStream
          setConfirmModal={setConfirmModal}
          id={handleId}
          setDeleteIntegration={setDeleteIntegration}
          setActionLoading={setActionLoading}
          getAdobeData={() => {}}
        />
      )}

      {!loading && confirmModal?.delete && (
        <ConfirmDeleteTealiumAudienceStream
          setConfirmModal={setConfirmModal}
          setDeleteIntegration={setDeleteIntegration}
        />
      )}
    </>
  );
};

export default TealiumAudienceStreamTable;
